import { createContext, useContext, useEffect, useState } from "react";
import {
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
} from "firebase/auth";
import { db, auth } from "../firebase";

import { apiUrl } from "../config";
import { useNavigate, useLocation } from "react-router-dom";

import { doc, getDoc, setDoc, onSnapshot } from "firebase/firestore";

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  useDisclosure,
} from "@chakra-ui/react";

const UserContext = createContext();

// Define public routes that don't require authentication
const publicRoutes = [
  "/",
  "/login",
  "/support",
  "/businesses",
  "/validate",
  "/terms",
  "/privacy",
  "/payment/success",
  "/payment/failure",
  "/payment/cancellation",
];

// Route single-affiliation users straight to their one destination so the
// homepage doesn't act as a dead-end (especially for users invited to a single
// campaign). Platform admins always land on /home — they need the switchboard.
// Exported so the navbar logo can replay this routing on click.
export const resolveLandingPath = (userData) => {
  if (userData?.isPlatformAdmin) {
    return "/home";
  }
  const businesses = userData?.businesses || [];
  const organizations = userData?.organizations || [];
  const campaigns = userData?.campaigns || [];
  const affiliations = businesses.length + organizations.length + campaigns.length;
  if (affiliations !== 1) {
    return "/home";
  }
  if (businesses.length === 1) {
    return `/business/${businesses[0]}/dashboard`;
  }
  if (organizations.length === 1) {
    return `/organization/${organizations[0]}`;
  }
  return `/campaign/${campaigns[0]}`;
};

export const AuthContextProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const uid = user.uid;
      const email = user.email;
      const firstName = user.displayName.split(" ")[0];
      const lastName = user.displayName.split(" ").slice(1).join(" ");

      // Check if user document exists, if not create one
      const userDocRef = doc(db, "users", uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        await setDoc(userDocRef, {
          firstName,
          lastName,
          email,
        });
      }
      const userData = userDoc.exists() ? userDoc.data() : {};

      sessionStorage.setItem("uid", uid);
      sessionStorage.setItem("firstName", firstName);
      sessionStorage.setItem("lastName", lastName);
      sessionStorage.setItem("email", email);
      sessionStorage.setItem("signInMethod", "google");

      setUser({
        uid,
        email,
        firstName,
        lastName,
        signInMethod: "google",
        isPlatformAdmin: userData.isPlatformAdmin === true,
        organizations: userData.organizations || [],
        businesses: userData.businesses || [],
        campaigns: userData.campaigns || [],
      });

      // Return user data instead of navigating
      return { uid, email, firstName, lastName };
    } catch (error) {
      console.error("Error signing in with Google:", error.message);
      throw error;
    }
  };

  const logout = async () => {
    if (sessionStorage.length !== 0) {
      sessionStorage.clear();
      await signOut(auth);
      setUser(null);
      navigate("/");
    }
  };

  const handleSessionExpired = () => {
    onOpen();
    logout();
  };

  const signUpWithEmail = async (email, password, firstName, lastName) => {
    try {
      const result = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = result.user;
      const uid = user.uid;

      // TEMP: email verification disabled
      // await sendEmailVerification(user);

      const userDocRef = doc(db, "users", uid);
      await setDoc(userDocRef, {
        firstName,
        lastName,
        email,
      });

      sessionStorage.setItem("uid", uid);
      sessionStorage.setItem("firstName", firstName);
      sessionStorage.setItem("lastName", lastName);
      sessionStorage.setItem("email", email);
      sessionStorage.setItem("signInMethod", "email");

      setUser({
        uid,
        email,
        firstName,
        lastName,
        signInMethod: "email",
      });

      // Return user data instead of navigating
      return { uid, email, firstName, lastName };
    } catch (error) {
      console.error("Error signing up with email:", error.message);
      throw error;
    }
  };

  const signInWithEmail = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const user = result.user;
      const uid = user.uid;

      // Get user data from Firestore
      const userDocRef = doc(db, "users", uid);
      const userDoc = await getDoc(userDocRef);
      const userData = userDoc.data();

      sessionStorage.setItem("uid", uid);
      sessionStorage.setItem("firstName", userData.firstName);
      sessionStorage.setItem("lastName", userData.lastName);
      sessionStorage.setItem("email", email);
      sessionStorage.setItem("signInMethod", "email");

      setUser({
        uid,
        email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        signInMethod: "email",
        isPlatformAdmin: userData.isPlatformAdmin === true,
        organizations: userData.organizations || [],
        businesses: userData.businesses || [],
        campaigns: userData.campaigns || [],
      });

      navigate(resolveLandingPath(userData));
    } catch (error) {
      console.error("Error signing in with email:", error.message);
      throw error;
    }
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error("Error sending password reset email:", error.message);
      throw error;
    }
  };

  useEffect(() => {
    let unsubscribeUser = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          let idToken = await currentUser.getIdToken(true);
          sessionStorage.setItem("idToken", idToken);

          // Set up token refresh interval
          currentUser.getIdToken(true).catch((error) => {
            if (error.code === "auth/id-token-expired") {
              handleSessionExpired();
            }
          });

          // Bootstrap platform-admin status from the backend allowlist. If a
          // claim is newly granted, force a token refresh so subsequent
          // requests carry it.
          try {
            const syncRes = await fetch(`${apiUrl}/admin/sync-claims`, {
              method: "POST",
              headers: {
                Authorization: `Bearer ${idToken}`,
                "Content-Type": "application/json",
              },
            });
            if (syncRes.ok) {
              const syncData = await syncRes.json();
              if (syncData.claimNewlySet) {
                idToken = await currentUser.getIdToken(true);
                sessionStorage.setItem("idToken", idToken);
              }
            }
          } catch (err) {
            console.warn("Platform admin sync failed", err);
          }

          const uid = currentUser.uid;
          const userDocRef = doc(db, "users", uid);

          // Set up real-time listener for user document
          unsubscribeUser = onSnapshot(userDocRef, (doc) => {
            if (doc.exists()) {
              const userData = doc.data();
              const signInMethod = sessionStorage.getItem("signInMethod");
              const userInfo = {
                uid,
                email: currentUser.email,
                firstName: userData.firstName,
                lastName: userData.lastName,
                signInMethod: signInMethod || "email",
                isPlatformAdmin: userData.isPlatformAdmin === true,
                organizations: userData.organizations || [],
                businesses: userData.businesses || [],
                campaigns: userData.campaigns || [],
              };
              setUser(userInfo);
              // Update session storage
              sessionStorage.setItem("uid", uid);
              sessionStorage.setItem("firstName", userData.firstName);
              sessionStorage.setItem("lastName", userData.lastName);
              sessionStorage.setItem("email", currentUser.email);
            }
          });
        } catch (error) {
          if (error.code === "auth/id-token-expired") {
            handleSessionExpired();
          }
        }
      } else {
        setUser(null);
        sessionStorage.clear();
        if (unsubscribeUser) {
          unsubscribeUser();
        }
      }
      setLoading(false);
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeUser) {
        unsubscribeUser();
      }
    };
  }, []);

  // Simplified navigation logic
  useEffect(() => {
    const isPublicRoute = publicRoutes.some((route) =>
      location.pathname.startsWith(route)
    );
    const isVerificationRoute = location.pathname === "/verify-email";

    if (!loading) {
      if (user) {
        // TEMP: email verification disabled — treat all users as verified
        // if (auth.currentUser?.emailVerified) {
        //   if (location.pathname === "/" || location.pathname === "/login") {
        //     navigate("/home");
        //   }
        // } else if (!isVerificationRoute) {
        //   navigate("/verify-email");
        // }
        if (
          location.pathname === "/" ||
          location.pathname === "/login" ||
          isVerificationRoute
        ) {
          navigate(resolveLandingPath(user));
        }
      } else if (!isPublicRoute && !isVerificationRoute) {
        navigate("/");
      }
    }
  }, [user, loading, navigate, location.pathname]);

  return (
    <UserContext.Provider
      value={{
        user,
        logout,
        signInWithGoogle,
        signUpWithEmail,
        signInWithEmail,
        resetPassword,
      }}
    >
      {!loading && children}

      {/* Session Expiration Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Session Expired</ModalHeader>
          <ModalBody>
            Your session has expired. Please log in again to continue.
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={() => {
                onClose();
                navigate("/login");
              }}
            >
              Log In
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </UserContext.Provider>
  );
};

export const UserAuth = () => {
  return useContext(UserContext);
};
